/**
 * VrsDetailBlock — FASE 10C.2
 *
 * Pannello admin che mostra il valore di ricostruzione fabbricato calcolato
 * silentemente dal motore VR (10A+10B). Riusabile in tutti i tab admin
 * (CAT NAT, Casa submissions, Commercio).
 *
 * Props:
 *  - data:           record completo (deve contenere `valore_ricostruzione_stimato`
 *                    e `valore_ricostruzione_audit`).
 *  - onRicalcola:    async () => void  - chiamata API admin di ricalcolo
 *  - title:          opzionale string (override titolo)
 */
import { AlertTriangle, RefreshCw, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const fmtEur = (n) => (typeof n === "number" ? "€ " + n.toLocaleString("it-IT") : "—");

export default function VrsDetailBlock({ data, onRicalcola, title = "Stima valore di ricostruzione (auto)" }) {
  const vrs = data?.valore_ricostruzione_stimato;
  const audit = data?.valore_ricostruzione_audit;
  const [busy, setBusy] = useState(false);

  if (!vrs && !audit) {
    return null; // mai calcolato
  }

  const handleRicalcola = async () => {
    if (!onRicalcola || busy) return;
    setBusy(true);
    try {
      await onRicalcola();
    } finally {
      setBusy(false);
    }
  };

  // Skip silente: nessun valore, solo audit con outcome=skipped
  if (!vrs && audit?.outcome === "skipped") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
        <p className="text-[11px] uppercase tracking-wide font-bold text-amber-800 flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> {title}
        </p>
        <p className="text-[12px] text-amber-900">
          ⚠️ Calcolo silente non eseguito: <strong>{audit.reason}</strong>.
          Compila i campi mancanti del fabbricato (superficie, indirizzo, tipologia) per abilitare la stima.
        </p>
        {onRicalcola && (
          <Button size="sm" variant="outline" onClick={handleRicalcola} disabled={busy} className="text-amber-800 border-amber-300">
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Ritenta calcolo
          </Button>
        )}
      </div>
    );
  }

  if (!vrs) return null;

  const range = vrs.range_eur || [vrs.costo_totale_netto_min, vrs.costo_totale_netto_max];
  const consigliato = vrs.valore_per_polizza_consigliato;
  const coef = (vrs.coefficienti_applicati || []).filter((c) => c.fattore && c.fattore !== 1);
  const geo = vrs.rischio_geo;
  const reviewVoci = vrs.needs_review_voci || [];

  return (
    <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4 space-y-3" data-testid="vrs-detail-block">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-800 flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> {title}
        </p>
        {onRicalcola && (
          <Button size="sm" variant="outline" onClick={handleRicalcola} disabled={busy} className="text-emerald-800 border-emerald-300 h-7" data-testid="vrs-ricalcola-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Ricalcola
          </Button>
        )}
      </div>

      {/* Banner advisory */}
      <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-900">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          Calcolo silente automatico — da validare con perizia per polizze &gt; 500.000 €.
          Le voci con flag <em>needs_review</em> usano valori indicativi, non definitivi.
        </span>
      </div>

      {/* KPI principali */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-white rounded-lg border border-emerald-100 px-2 py-2">
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51]">Min</p>
          <p className="text-sm font-bold text-[#163321]">{fmtEur(range?.[0])}</p>
        </div>
        <div className="bg-emerald-100/60 rounded-lg border border-emerald-300 px-2 py-2">
          <p className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">Consigliato</p>
          <p className="text-sm font-bold text-emerald-900">{fmtEur(consigliato)}</p>
        </div>
        <div className="bg-white rounded-lg border border-emerald-100 px-2 py-2">
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51]">Max</p>
          <p className="text-sm font-bold text-[#163321]">{fmtEur(range?.[1])}</p>
        </div>
      </div>

      {/* Dettaglio tecnico */}
      <div className="grid sm:grid-cols-2 gap-3 text-[11px] text-[#163321]">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold">Tipologia motore</p>
          <p>{vrs.tipologia} ({vrs.livello_finiture})</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold">Superficie</p>
          <p>{vrs.superficie_mq} mq</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold">Costo unitario modulato</p>
          <p>{fmtEur(vrs.costo_unitario_modulato_eur_mq)} /mq</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold">Moltiplicatore totale</p>
          <p>{vrs.moltiplicatore_totale?.toFixed?.(3) || vrs.moltiplicatore_totale}</p>
        </div>
        {vrs.range_netto_eur && Array.isArray(vrs.range_netto_eur) && (
          <div className="sm:col-span-2 pt-1 border-t border-emerald-100">
            <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold">
              Range netto base (senza IVA / contributo / spese tecniche)
            </p>
            <p className="text-[11px] text-[#4A5E51]">
              {fmtEur(vrs.range_netto_eur[0])} – {fmtEur(vrs.range_netto_eur[1])}
              <span className="text-[10px] text-[#7A8A80] ml-1">(per perizia tecnica IVASS)</span>
            </p>
          </div>
        )}
      </div>

      {/* Profilo geo */}
      {geo && (
        <div className="bg-white border border-emerald-100 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold mb-1">Profilo rischio geo</p>
          <p className="text-[12px]">Score: <strong>{geo.score_0_100}/100</strong></p>
          {geo.breakdown && (
            <p className="text-[10px] text-[#4A5E51]">
              S {geo.breakdown.sismica}, I {geo.breakdown.idraulica}, F {geo.breakdown.frane}, C {geo.breakdown.clima}, K {geo.breakdown.costa}
            </p>
          )}
        </div>
      )}

      {/* Coefficienti applicati */}
      {coef.length > 0 && (
        <div className="bg-white border border-emerald-100 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] font-semibold mb-1">Coefficienti applicati</p>
          <ul className="text-[11px] space-y-0.5">
            {coef.map((c, i) => (
              <li key={i} className="flex justify-between">
                <span className="text-[#163321]">
                  {c.voce} {c.needs_review && <em className="text-amber-700">(da rivedere)</em>}
                </span>
                <strong>×{c.fattore?.toFixed?.(3) || c.fattore}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Audit */}
      {audit && (
        <p className="text-[10px] text-[#4A5E51] italic">
          Calcolato il {new Date(audit.ts).toLocaleString("it-IT")}{audit.actor ? ` da ${audit.actor}` : ""} — {audit.geo_modulato ? "geo applicata" : "no geo"}.
          {reviewVoci.length > 0 && ` ⚠ ${reviewVoci.length} voce(i) needs_review.`}
        </p>
      )}
    </div>
  );
}
